import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import '../../style/speakSearch.css';
import Header from '../../component/header';
import WhatsappSlipButton from '../../component/WhatsappSlipButton';
import PracharButton from '../../component/PracharButton';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const OrganizationDetail = () => {
    const [activeTab, setActiveTab] = useState('pramukh');
    const [loading, setLoading] = useState(true);
    const [boothPramukhs, setBoothPramukhs] = useState([]);
    const [voters, setVoters] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState('all'); // 'all', 'survey_available', 'survey_not_available', 'survey_pending'
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

    // --- Create Modal State ---
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        booth_no: '',
        name: '',
        mobile_no: '',
        user_type: 'BP'
    });
    const [createMobileError, setCreateMobileError] = useState("");

    // --- Edit Modal State ---
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editFormData, setEditFormData] = useState({
        user_id: '',
        name: '',
        mobile_no: '',
        user_type: ''
    });
    const [editMobileError, setEditMobileError] = useState("");

    // --- Profile Detail Modal State ---
    const [selectedUser, setSelectedUser] = useState(null);

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    const { appId, language, userId, userName } = useAppId();
    const navigate = useNavigate();
    const location = useLocation();

    const currentBoothNo = location.state?.selectedBoothNo || localStorage.getItem('boothNo') || "";

    const lang = language || 'en';

    // Helper function to convert Status 'D', 'C', 'P', 'N' to Full Form
    const getStatusFullForm = (statusCode) => {
        switch (statusCode?.toUpperCase()) {
            case 'D': return 'Doubtful';
            case 'C': return "Can't Say";
            case 'P': return 'Positive';
            case 'N': return 'Negative';
            default: return statusCode || 'Voted';
        }
    };

    // --- Edit Mobile State ---
    const [isEditMobileOpen, setIsEditMobileOpen] = useState(false);
    const [editingVoter, setEditingVoter] = useState(null);
    const [newMobile, setNewMobile] = useState('');
    const [isSavingMobile, setIsSavingMobile] = useState(false);



    // --- Edit Mobile API Call ---
    const handleUpdateMobile = async () => {
        if (!newMobile || newMobile.length !== 10) {
            alert("Please enter a valid 10-digit mobile number.");
            return;
        }

        setIsSavingMobile(true);
        try {
            const payload = {
                app_id: appId?.toString(),
                // 🔴 FIX: અહિયાં id ની જગ્યાએ idcard_no પાસ કર્યું છે
                voter_id: editingVoter.idcard_no,
                mobile_no: newMobile,
                modify_by: userId?.toString() || "1"
            };

            const response = await axios.post(`${BASE_URL}/api/upd_voter_mobile`, payload);

            if (response.data.success || response.data.Success === "1") {
                alert("Mobile number updated successfully!");
                // નવો ડેટા લાવ્યા વગર જ સીધું UI અપડેટ કરો
                setVoters(prev => prev.map(v =>
                    v.id === editingVoter.id ? { ...v, contact_no: newMobile } : v
                ));
                setIsEditMobileOpen(false);
            } else {
                alert("Failed to update mobile number.");
            }
        } catch (error) {
            console.error("Error updating mobile:", error);
            alert("An error occurred while updating the mobile number.");
        } finally {
            setIsSavingMobile(false);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isFilterDropdownOpen && !event.target.closest('.filter-dropdown-container')) {
                setIsFilterDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isFilterDropdownOpen]);

    // Get filter button text
    const getFilterButtonText = () => {
        switch (filterType) {
            case 'survey_available': return "Survey Available";
            case 'survey_not_available': return "Survey Not Available";
            case 'survey_pending': return "Survey Pending";
            default: return "All";
        }
    };

    // Generate WhatsApp message in Gujarati for Pramukh
    const generateWhatsAppMessage = (pramukhName, userType) => {
        const loginUserName = userName || 'Admin';
        const designation = userType === 'BP' ? 'બૂથ પ્રમુખ' : 'સહ બૂથ પ્રમુખ';
        return `નમસ્તે ${pramukhName}, ${loginUserName} એ તમને ${designation}ની જવાબદારી સોંપેલી છે.`;
    };

    // Generate SMS message in Gujarati for Pramukh
    const generateSMSMessage = (pramukhName, userType) => {
        const loginUserName = userName || 'Admin';
        const designation = userType === 'BP' ? 'બૂથ પ્રમુખ' : 'સહ બૂથ પ્રમુખ';
        return `નમસ્તે ${pramukhName}, ${loginUserName} એ તમને ${designation}ની જવાબદારી સોંપેલી છે.`;
    };

    const t = {
        en: {
            pageTitle: "Organization Management",
            subtitlePrefix: "Details for Booth No:",
            tabPramukh: "Organization",
            tabVoters: "Voters",
            searchPramukh: "Search Pramukh...",
            searchVoter: "Search Voter...",
            loadingData: "Loading Data...",
            noPramukh: "No Pramukh found.",
            noVoters: "No Voters Found.",
            tablePhoto: "Photo",
            tableName: "Name",
            tableDesig: "Designation",
            tableContact: "Contact No",
            tableActions: "Actions",
            addPramukhBtn: "Add Booth Pramukh",
            addSahPramukhBtn: "Add Sah Booth Pramukh",
            boothPramukhTitle: "Booth Pramukh",
            sahBoothPramukhTitle: "Sah Booth Pramukh",
            fatherHusband: "F/S :",
            address: "Address :",
            serialNo: "Sr No. :",
            mobile: "Mobile :",
            idCardNo: "ID Card No :",
            boothNo: "Booth No :",
            houseNo: "House No :",
            pollingLoc: "Voting Loc :",
            call: "Call",
            text: "Text",
            slip: "Slip",
            prachar: "Prachar",
            print: "Print",
            family: "Family",
            addSurvey: "Add Survey",
            all: "All",
            surveyAvailable: "Survey Available",
            surveyNotAvailable: "Survey Not Available",
            surveyPending: "Survey Pending",
            modalTitleBP: "Create Booth Pramukh",
            modalTitleBS: "Create Sah Booth Pramukh",
            nameLabel: "Full Name",
            mobileLabel: "Mobile Number",
            cancelBtn: "Cancel",
            submitBtn: "Create",
            submitting: "Creating...",
            editModalTitle: "Edit Pramukh",
            updateBtn: "Update",
            updatingBtn: "Updating...",
            sendWhatsapp: "Send Responsibility in Whatsapp",
            sendSMS: "Send Responsibility in SMS",
            mobileRequired: "Mobile number is required",
            mobileLength: "Mobile number must be 10 digits",
            mobileNumeric: "Mobile number must contain only digits",
            mobileInvalid: "Please enter a valid Indian mobile number (starts with 6,7,8,9)",
            fillAllFields: "Please fill in all fields",
            validMobile: "Please enter a valid 10-digit mobile number",
            deleteConfirm: "Are you sure you want to delete this user?",
            deleteSuccess: "User deleted successfully!",
            deleteFailed: "Failed to delete user.",
            deleteError: "Error deleting user.",
            userIdMissing: "Error: User ID is missing."
        },
        hi: {
            pageTitle: "संगठन प्रबंधन",
            subtitlePrefix: "बूथ नं. के लिए विवरण:",
            tabPramukh: "संगठन",
            tabVoters: "मतदाता",
            searchPramukh: "प्रमुख खोजें...",
            searchVoter: "मतदाता खोजें...",
            loadingData: "डेटा लोड हो रहा है...",
            noPramukh: "कोई प्रमुख नहीं मिला।",
            noVoters: "कोई मतदाता नहीं मिला।",
            tablePhoto: "फोटो",
            tableName: "नाम",
            tableDesig: "पदनाम",
            tableContact: "संपर्क नंबर",
            tableActions: "कार्रवाइयां",
            addPramukhBtn: "बूथ प्रमुख जोड़ें",
            addSahPramukhBtn: "सह बूथ प्रमुख जोड़ें",
            boothPramukhTitle: "बूथ प्रमुख",
            sahBoothPramukhTitle: "सह बूथ प्रमुख",
            fatherHusband: "पिता/पति :",
            address: "पता :",
            serialNo: "क्रमांक :",
            mobile: "मोबाइल :",
            idCardNo: "पहचान पत्र नं.:",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLoc: "मतदान स्थान :",
            call: "कॉल",
            text: "मैसेज",
            slip: "पर्ची",
            prachar: "प्रचार",
            print: "प्रिंट",
            family: "परिवार",
            addSurvey: "सर्वेक्षण जोड़ें",
            all: "सभी",
            surveyAvailable: "सर्वेक्षण उपलब्ध",
            surveyNotAvailable: "सर्वेक्षण अनुपलब्ध",
            surveyPending: "सर्वेक्षण लंबित",
            modalTitleBP: "बूथ प्रमुख बनाएं",
            modalTitleBS: "सह बूथ प्रमुख बनाएं",
            nameLabel: "पूरा नाम",
            mobileLabel: "मोबाइल नंबर",
            cancelBtn: "रद्द करें",
            submitBtn: "बनाएं",
            submitting: "बना रहा है...",
            editModalTitle: "प्रमुख संपादित करें",
            updateBtn: "अपडेट करें",
            updatingBtn: "अपडेट कर रहा है...",
            sendWhatsapp: "व्हाट्सएप में जिम्मेदारी भेजें",
            sendSMS: "एसएमएस में जिम्मेदारी भेजें",
            mobileRequired: "मोबाइल नंबर आवश्यक है",
            mobileLength: "मोबाइल नंबर 10 अंकों का होना चाहिए",
            mobileNumeric: "मोबाइल नंबर में केवल अंक होने चाहिए",
            mobileInvalid: "कृपया एक वैध भारतीय मोबाइल नंबर दर्ज करें (6,7,8,9 से शुरू)",
            fillAllFields: "कृपया सभी फ़ील्ड भरें",
            validMobile: "कृपया 10 अंकों का मोबाइल नंबर दर्ज करें",
            deleteConfirm: "क्या आप वाकई इस उपयोगकर्ता को हटाना चाहते हैं?",
            deleteSuccess: "उपयोगकर्ता सफलतापूर्वक हटा दिया गया!",
            deleteFailed: "उपयोगकर्ता को हटाने में विफल।",
            deleteError: "उपयोगकर्ता को हटाने में त्रुटि।",
            userIdMissing: "त्रुटि: उपयोगकर्ता आईडी गायब है।"
        },
        gu: {
            pageTitle: "સંગઠન વ્યવસ્થાપન",
            subtitlePrefix: "બૂથ નંબર માટે વિગતો:",
            tabPramukh: "સંગઠન",
            tabVoters: "મતદારો",
            searchPramukh: "પ્રમુખ શોધો...",
            searchVoter: "મતદાર શોધો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noPramukh: "કોઈ પ્રમુખ મળ્યો નથી.",
            noVoters: "કોઈ મતદાર મળ્યા નથી.",
            tablePhoto: "ફોટો",
            tableName: "નામ",
            tableDesig: "હોદ્દો",
            tableContact: "સંપર્ક નંબર",
            tableActions: "ક્રિયાઓ",
            addPramukhBtn: "બૂથ પ્રમુખ ઉમેરો",
            addSahPramukhBtn: "સહ બૂથ પ્રમુખ ઉમેરો",
            boothPramukhTitle: "બૂથ પ્રમુખ",
            sahBoothPramukhTitle: "સહ બૂથ પ્રમુખ",
            fatherHusband: "પિતા/પતિ :",
            address: "સરનામું :",
            serialNo: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCardNo: "ઓળખ કાર્ડ નં :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLoc: "મતદાન સ્થળ :",
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            prachar: "પ્રચાર",
            print: "પ્રિન્ટ",
            family: "કુટુંબ",
            addSurvey: "સર્વે ઉમેરો",
            all: "બધા",
            surveyAvailable: "સર્વે ઉપલબ્ધ",
            surveyNotAvailable: "સર્વે અનુપલબ્ધ",
            surveyPending: "સર્વે બાકી",
            modalTitleBP: "બૂથ પ્રમુખ બનાવો",
            modalTitleBS: "સહ બૂથ પ્રમુખ બનાવો",
            nameLabel: "પૂરું નામ",
            mobileLabel: "મોબાઇલ નંબર",
            cancelBtn: "રદ કરો",
            submitBtn: "બનાવો",
            submitting: "બનાવી રહ્યા છીએ...",
            editModalTitle: "પ્રમુખ સંપાદિત કરો",
            updateBtn: "અપડેટ કરો",
            updatingBtn: "અપડેટ કરી રહ્યા છીએ...",
            sendWhatsapp: "વોટ્સએપમાં જવાબદારી મોકલો",
            sendSMS: "એસએમએસમાં જવાબદારી મોકલો",
            mobileRequired: "મોબાઇલ નંબર જરૂરી છે",
            mobileLength: "મોબાઇલ નંબર 10 અંકનો હોવો જોઈએ",
            mobileNumeric: "મોબાઇલ નંબરમાં માત્ર અંકો હોવા જોઈએ",
            mobileInvalid: "કૃપા કરીને માન્ય ભારતીય મોબાઇલ નંબર દાખલ કરો (6,7,8,9 થી શરૂ)",
            fillAllFields: "કૃપા કરીને બધા ફીલ્ડ ભરો",
            validMobile: "કૃપા કરીને 10 અંકનો માન્ય મોબાઇલ નંબર દાખલ કરો",
            deleteConfirm: "શું તમે ખરેખર આ વપરાશકર્તાને કાઢી નાખવા માંગો છો?",
            deleteSuccess: "વપરાશકર્તા સફળતાપૂર્વક કાઢી નાખવામાં આવ્યો!",
            deleteFailed: "વપરાશકર્તા કાઢી નાખવામાં નિષ્ફળ.",
            deleteError: "વપરાશકર્તા કાઢી નાખવામાં ભૂલ.",
            userIdMissing: "ભૂલ: વપરાશકર્તા ID ગુમ થયેલ છે."
        }
    };

    const txt = t[lang];

    // Mobile number validation function
    const validateMobileNumber = (number) => {
        if (!number) {
            return { isValid: false, message: txt.mobileRequired };
        }
        if (!/^\d{10}$/.test(number)) {
            return { isValid: false, message: txt.mobileLength };
        }
        if (!/^\d+$/.test(number)) {
            return { isValid: false, message: txt.mobileNumeric };
        }
        const firstDigit = number.charAt(0);
        if (!['6', '7', '8', '9'].includes(firstDigit)) {
            return { isValid: false, message: txt.mobileInvalid };
        }
        return { isValid: true, message: "" };
    };

    useEffect(() => {
        if (!appId) { navigate("/"); return; }
        if (currentBoothNo) { fetchData(); }
    }, [appId, navigate, currentBoothNo]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_booth_pramukh_cadre_with_voter`,
                { app_id: appId, booth_no: currentBoothNo }
            );
            if (response.data.success) {
                setBoothPramukhs(response.data.data.result || []);
                setVoters(response.data.data.result2 || []);
            }
        } catch (error) { console.error("Error fetching data:", error); }
        finally { setLoading(false); }
    };

    const handleOpenCreateModal = (type) => {
        setCreateFormData({ booth_no: currentBoothNo, name: '', mobile_no: '', user_type: type });
        setCreateMobileError("");
        setIsCreateModalOpen(true);
    };

    const handleCreateInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'mobile_no') {
            const numericValue = value.replace(/[^0-9]/g, '');
            const limitedValue = numericValue.slice(0, 10);
            setCreateFormData(prev => ({ ...prev, [name]: limitedValue }));

            if (createMobileError) setCreateMobileError("");

            if (limitedValue.length === 10) {
                const validation = validateMobileNumber(limitedValue);
                if (!validation.isValid) {
                    setCreateMobileError(validation.message);
                } else {
                    setCreateMobileError("");
                }
            } else if (limitedValue.length > 0 && limitedValue.length < 10) {
                setCreateMobileError(txt.mobileLength);
            }
        } else {
            setCreateFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        if (!createFormData.name || !createFormData.mobile_no) {
            alert(txt.fillAllFields);
            return;
        }

        const validation = validateMobileNumber(createFormData.mobile_no);
        if (!validation.isValid) {
            alert(validation.message);
            setCreateMobileError(validation.message);
            return;
        }

        setIsSubmitting(true);
        const payload = {
            app_id: appId,
            name: createFormData.name,
            mobile_no: createFormData.mobile_no,
            user_type: createFormData.user_type,
            booth_no: createFormData.booth_no,
            booth_list: "",
            create_by: userId
        };

        try {
            const response = await axios.post(`${BASE_URL}/api/insert_user`, payload);
            if (response.data.success) {
                alert("Success!");
                setIsCreateModalOpen(false);
                setCreateMobileError("");
                fetchData();
            } else {
                alert("Error: " + JSON.stringify(response.data));
            }
        } catch (error) {
            alert("Failed to create.");
        }
        finally {
            setIsSubmitting(false);
        }
    };

    // --- Edit Handlers ---
    const handleEditClick = () => {
        if (selectedUser) {
            setEditFormData({
                user_id: selectedUser.user_id,
                name: selectedUser.name,
                mobile_no: selectedUser.mobile_no,
                user_type: selectedUser.user_type
            });
            setEditMobileError("");
            setSelectedUser(null);
            setIsEditModalOpen(true);
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'mobile_no') {
            const numericValue = value.replace(/[^0-9]/g, '');
            const limitedValue = numericValue.slice(0, 10);
            setEditFormData(prev => ({ ...prev, [name]: limitedValue }));

            if (editMobileError) setEditMobileError("");

            if (limitedValue.length === 10) {
                const validation = validateMobileNumber(limitedValue);
                if (!validation.isValid) {
                    setEditMobileError(validation.message);
                } else {
                    setEditMobileError("");
                }
            } else if (limitedValue.length > 0 && limitedValue.length < 10) {
                setEditMobileError(txt.mobileLength);
            }
        } else {
            setEditFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();

        if (!editFormData.name || !editFormData.mobile_no) {
            alert(txt.fillAllFields);
            return;
        }

        const validation = validateMobileNumber(editFormData.mobile_no);
        if (!validation.isValid) {
            alert(validation.message);
            setEditMobileError(validation.message);
            return;
        }

        setIsEditing(true);

        const payload = {
            user_id: editFormData.user_id,
            app_id: appId,
            name: editFormData.name,
            mobile_no: editFormData.mobile_no,
            user_type: editFormData.user_type,
            booth_no: currentBoothNo,
            booth_list: "",
            create_by: userId
        };

        try {
            const response = await axios.post(`${BASE_URL}/api/update_user`, payload);
            if (response.data.success && response.data.data && response.data.data.Success == "1") {
                alert("Updated successfully!");
                setIsEditModalOpen(false);
                setEditMobileError("");
                fetchData();
            } else {
                alert(response.data.data?.Message || "Failed to update.");
            }
        } catch (error) {
            console.error("Axios Error:", error);
            alert(error.response?.data?.error || "Error updating.");
        } finally {
            setIsEditing(false);
        }
    };

    // --- Delete Handler ---
    const handleDeleteUser = async (id) => {
        if (!id) {
            alert(txt.userIdMissing);
            return;
        }

        if (window.confirm(txt.deleteConfirm)) {
            try {
                const response = await axios.post(`${BASE_URL}/api/dlt_user`, {
                    app_id: appId,
                    user_id: id,
                    delete_by: userId
                });

                if (response.data.success || response.data.Success == "1" || (response.data.data && response.data.data.Success == "1")) {
                    alert(txt.deleteSuccess);
                    setSelectedUser(null);
                    fetchData();
                } else {
                    alert(response.data.message || response.data.Message || response.data.data?.Message || txt.deleteFailed);
                }
            } catch (error) {
                console.error("Delete Error:", error);
                alert(txt.deleteError);
            }
        }
    };

    // Filter voters by filterType and search term
    const getFilteredData = () => {
        const lowerSearch = searchTerm.toLowerCase();

        if (activeTab === 'pramukh') {
            return boothPramukhs.filter(item =>
                (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
                (item.mobile_no && item.mobile_no.includes(lowerSearch))
            );
        } else {
            let filtered = [...voters];

            // Apply survey status filter
            if (filterType === 'survey_available') {
                filtered = filtered.filter(voter => voter.voter_available === true);
            } else if (filterType === 'survey_not_available') {
                filtered = filtered.filter(voter => voter.voter_available === false);
            } else if (filterType === 'survey_pending') {
                filtered = filtered.filter(voter => voter.voter_available === null);
            }

            // Apply search term filter
            if (searchTerm) {
                filtered = filtered.filter(item =>
                    (item.eng_f_name && item.eng_f_name.toLowerCase().includes(lowerSearch)) ||
                    (item.eng_surname && item.eng_surname.toLowerCase().includes(lowerSearch)) ||
                    (item.contact_no && item.contact_no.includes(lowerSearch))
                );
            }

            return filtered;
        }
    };

    const filteredList = getFilteredData();
    const bpItems = filteredList.filter(item => item.user_type === 'BP');
    const bsItems = filteredList.filter(item => item.user_type === 'BS');

    // Pagination for voters tab
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = activeTab === 'voters' ? filteredList.slice(indexOfFirstItem, indexOfLastItem) : filteredList;
    const totalPages = Math.ceil(filteredList.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchTerm("");
        setFilterType('all');
        setCurrentPage(1);
    };

    // Handle filter click
    const handleFilterClick = (filter) => {
        setFilterType(filter);
        setCurrentPage(1);
        setIsFilterDropdownOpen(false);
    };

    // --- WhatsApp Message Generation Logic ---
    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available for this voter.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: userId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${member.idcard_no}\n`;
                    message += `મતદાન સ્થળ : ${member.eng_polling_location}\n`;
                    message += `મતદાન તા. : ૨૬-૦૪-૨૦૨૬, રવિવાર \n સમય : સવારે ૭.૦૦ થી સાંજે ૬.૦૦ સુધી\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;

                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');

            } else {
                alert("Could not fetch family details.");
            }
        } catch (error) {
            console.error("Error generating WhatsApp slip:", error);
            alert("Error generating message.");
        }
    };

    // --- SMS Message Generation Logic ---
    const handleSmsShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available for this voter.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_sms`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: userId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${member.idcard_no}\n`;
                    message += `મતદાન સ્થળ : ${member.eng_polling_location}\n`;
                    message += `મતદાન તા. : ૨૬-૦૪-૨૦૨૬, રવિવાર \n સમય : સવારે ૭.૦૦ થી સાંજે ૬.૦૦ સુધી\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');

                window.location.href = `sms:${mobile}?body=${encodedMessage}`;

            } else {
                alert("Could not fetch family details for SMS.");
            }
        } catch (error) {
            console.error("Error generating SMS slip:", error);
            alert("Error generating SMS message.");
        }
    };

    // Helper to render organization section
    const renderOrganizationSection = (data, title, buttonLabel, type) => (
        <div className="section-block mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="section-title-fixed m-0">{title}</h3>
                <button className="btn-add-user-small" onClick={() => handleOpenCreateModal(type)}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                    {buttonLabel}
                </button>
            </div>

            {data.length > 0 && (
                <div className="desktop-view table-responsive bg-white rounded-3 shadow-sm border">
                    <table className="table align-middle mb-0">
                        <thead className="bg-light text-muted small text-uppercase">
                            <tr>
                                <th className="p-3 ps-4">{txt.tablePhoto}</th>
                                <th className="p-3">{txt.tableName}</th>
                                <th className="p-3">{txt.tableDesig}</th>
                                <th className="p-3">{txt.tableContact}</th>
                                <th className="p-3 text-end pe-4">{txt.tableActions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item) => (
                                <tr
                                    key={item.user_id}
                                    className="border-bottom hover-row"
                                    onClick={() => setSelectedUser(item)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className="p-3 ps-4">
                                        <div className="avatar-circle">
                                            {item.photo ? <img src={item.photo} alt={item.name} className="avatar-img" /> : <span className="avatar-placeholder">{item.name?.charAt(0)}</span>}
                                        </div>
                                    </td>
                                    <td className="p-3"><span className="fw-bold text-dark">{item.name}</span></td>
                                    <td className="p-3"><span className="badge-designation">{item.designation}</span></td>
                                    <td className="p-3 text-muted font-monospace">{item.mobile_no}</td>
                                    <td className="p-3 text-end pe-4">
                                        <div className="d-flex gap-2 justify-content-end">
                                            <a href={`tel:${item.mobile_no}`} className="action-circle-btn btn-blue" onClick={(e) => e.stopPropagation()}><svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg></a>
                                            <a href={`https://wa.me/91${item.mobile_no}`} className="action-circle-btn btn-green" onClick={(e) => e.stopPropagation()}><svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01C17.18 3.03 14.69 2 12.04 2z" /></svg></a>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {data.length > 0 && (
                <div className="mobile-view">
                    {data.map((item) => (
                        <div
                            key={item.user_id}
                            className="bg-white rounded-3 shadow-sm border p-3 mb-3 d-flex align-items-center gap-3"
                            onClick={() => setSelectedUser(item)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="avatar-circle flex-shrink-0" style={{ width: '50px', height: '50px' }}>
                                {item.photo ? <img src={item.photo} alt={item.name} className="avatar-img" /> : <span className="avatar-placeholder">{item.name?.charAt(0)}</span>}
                            </div>
                            <div className="flex-grow-1">
                                <h6 className="mb-1 fw-bold text-dark">{item.name}</h6>
                                <span className="badge-designation">{item.designation}</span>
                                <br /><small className="text-muted font-monospace">{item.mobile_no}</small>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                <a href={`tel:${item.mobile_no}`} className="action-circle-btn btn-blue" onClick={(e) => e.stopPropagation()}><svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg></a>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className={`app-container p-4`}>
                <Header title={txt.pageTitle} />
                <div className="subtitle-container mb-4">
                    <p className="subtitle-text">
                        <span> {txt.subtitlePrefix} {currentBoothNo}</span>
                    </p>
                </div>
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                    <style jsx>{`
                        .app-container { background: #f8fafc; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                        .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                        .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                        .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                    `}</style>
                </div>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            <Header title={txt.pageTitle} />

            <div className="subtitle-container mb-4">
                <p className="subtitle-text">
                    <span> {txt.subtitlePrefix} {currentBoothNo}</span>
                </p>
            </div>

            {/* Mobile Number Edit Modal */}
            {isEditMobileOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setIsEditMobileOpen(false)}>
                    <div className="slide-up-element" style={{ background: 'white', padding: '24px', borderRadius: '16px', width: '90%', maxWidth: '350px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={(e) => e.stopPropagation()}>
                        <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>Update Mobile Number</h4>
                        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b' }}>{editingVoter?.eng_f_name} {editingVoter?.eng_surname}</p>

                        <div style={{ marginBottom: '24px' }}>
                            <input
                                type="tel"
                                maxLength="10"
                                value={newMobile}
                                onChange={(e) => setNewMobile(e.target.value.replace(/[^0-9]/g, ''))}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: (newMobile && !/^[6-9]\d{9}$/.test(newMobile) && newMobile.length === 10) ? '1px solid #ef4444' : '1px solid #cbd5e1',
                                    borderRadius: '10px',
                                    fontSize: '15px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                placeholder="Enter 10-digit mobile number"
                                autoFocus
                            />
                            {/* 🔴 વેલિડેશન એરર મેસેજ */}
                            {newMobile && newMobile.length > 0 && !/^[6-9]\d{9}$/.test(newMobile) && (
                                <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '6px', fontWeight: '500' }}>
                                    {newMobile.length < 10
                                        ? "Mobile number must be 10 digits"
                                        : "Number must start with 6, 7, 8, or 9"}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => setIsEditMobileOpen(false)}
                                style={{ padding: '10px 16px', background: 'white', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateMobile}
                                // 🔴 જો નંબર વેલિડ ન હોય તો બટન Disabled રહેશે
                                disabled={isSavingMobile || !/^[6-9]\d{9}$/.test(newMobile)}
                                style={{
                                    padding: '10px 20px',
                                    background: (!/^[6-9]\d{9}$/.test(newMobile)) ? '#94a3b8' : '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: (!/^[6-9]\d{9}$/.test(newMobile)) ? 'not-allowed' : 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                {isSavingMobile ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Create Modal */}
            {isCreateModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content-box" style={{ maxWidth: '400px' }}>
                        <div className="modal-header p-3">
                            <div>
                                <h2>{createFormData.user_type === 'BP' ? txt.modalTitleBP : txt.modalTitleBS}</h2>
                            </div>
                            <button className="close-btn-icon" onClick={() => { setIsCreateModalOpen(false); setCreateFormData({ booth_no: currentBoothNo, name: '', mobile_no: '', user_type: createFormData.user_type }); setCreateMobileError(""); }}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleCreateSubmit}>
                            <div className="modal-body-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '20px 0' }}>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.boothNo}</label>
                                    <input type="text" value={createFormData.booth_no} disabled className="form-input" style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f3f4f6' }} />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.nameLabel}</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={createFormData.name}
                                        onChange={handleCreateInputChange}
                                        className="form-input"
                                        style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }}
                                        required
                                    />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.mobileLabel}</label>
                                    <input
                                        type="text"
                                        name="mobile_no"
                                        value={createFormData.mobile_no}
                                        onChange={handleCreateInputChange}
                                        maxLength="10"
                                        className="form-input"
                                        style={{
                                            padding: '10px 15px',
                                            borderRadius: '8px',
                                            border: createMobileError ? '1px solid #ef4444' : '1px solid #d1d5db',
                                            outline: 'none'
                                        }}
                                        required
                                    />
                                    {createMobileError && (
                                        <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                            {createMobileError}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" className="btn-cancel" onClick={() => { setIsCreateModalOpen(false); setCreateFormData({ booth_no: currentBoothNo, name: '', mobile_no: '', user_type: createFormData.user_type }); setCreateMobileError(""); }} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white' }}>
                                    {txt.cancelBtn}
                                </button>
                                <button type="submit" className="btn-search-main" disabled={isSubmitting} style={{ padding: '10px 20px', borderRadius: '8px', background: '#2563eb', color: 'white' }}>
                                    {isSubmitting ? txt.submitting : txt.submitBtn}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Pramukh Modal */}
            {isEditModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content-box" style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <div><h2>{txt.editModalTitle}</h2></div>
                            <button className="close-btn-icon" onClick={() => setIsEditModalOpen(false)}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleUpdateSubmit}>
                            <div className="modal-body-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '20px 0' }}>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.nameLabel}</label>
                                    <input type="text" name="name" value={editFormData.name} onChange={handleEditInputChange} className="form-input" style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }} required />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.mobileLabel}</label>
                                    <input
                                        type="text"
                                        name="mobile_no"
                                        value={editFormData.mobile_no}
                                        onChange={handleEditInputChange}
                                        maxLength="10"
                                        className="form-input"
                                        style={{
                                            padding: '10px 15px',
                                            borderRadius: '8px',
                                            border: editMobileError ? '1px solid #ef4444' : '1px solid #d1d5db',
                                            outline: 'none'
                                        }}
                                        required
                                    />
                                    {editMobileError && (
                                        <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                            {editMobileError}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                                <button type="button" className="btn-cancel" onClick={() => setIsEditModalOpen(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>{txt.cancelBtn}</button>
                                <button type="submit" className="btn-search-main" disabled={isEditing} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#f59e0b', color: 'white', cursor: isEditing ? 'not-allowed' : 'pointer' }}>{isEditing ? txt.updatingBtn : txt.updateBtn}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Profile Detail Modal */}
            {selectedUser && (
                <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
                    <div className="profile-detail-container" onClick={(e) => e.stopPropagation()}>
                        <button className="profile-close-btn" onClick={() => setSelectedUser(null)}>
                            <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        <div className="profile-detail-card">
                            <div className="profile-avatar-wrapper">
                                <div className="profile-avatar-bg">
                                    <img src={selectedUser.photo ? selectedUser.photo : `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.name)}&background=e5e5e5&color=a3a3a3`} alt={selectedUser.name} />
                                </div>
                            </div>
                            <div className="profile-content">
                                <h2 className="profile-name">{selectedUser.name}</h2>
                                <p className="profile-mobile">{selectedUser.mobile_no}</p>
                                <div className="profile-big-btns">
                                    <a href={`https://wa.me/91${selectedUser.mobile_no}?text=${encodeURIComponent(generateWhatsAppMessage(selectedUser.name, selectedUser.user_type))}`} target="_blank" rel="noreferrer" className="btn-resp btn-wa">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                                        {txt.sendWhatsapp}
                                    </a>
                                    <a href={`sms:${selectedUser.mobile_no}?body=${encodeURIComponent(generateSMSMessage(selectedUser.name, selectedUser.user_type))}`} className="btn-resp btn-sms-big">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2 2z"></path></svg>
                                        {txt.sendSMS}
                                    </a>
                                </div>
                            </div>
                            <div className="profile-bottom-bar">
                                <a href={`tel:${selectedUser.mobile_no}`} className="btm-icon-wrapper">
                                    <div className="icon-transparent" style={{ color: '#0ea5e9' }}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                                    </div>
                                </a>
                                <div className="divider"></div>
                                <a href={`https://wa.me/91${selectedUser.mobile_no}`} target="_blank" rel="noreferrer" className="btm-icon-wrapper">
                                    <div className="icon-transparent" style={{ color: '#22c55e' }}>
                                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                                    </div>
                                </a>
                                <div className="divider"></div>
                                <a href={`sms:${selectedUser.mobile_no}`} className="btm-icon-wrapper">
                                    <div className="icon-transparent" style={{ color: '#3b82f6', position: 'relative' }}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 10H6v-2h12v2zm0-3H6V7h12v2z" /></svg>
                                        <span style={{ position: 'absolute', fontSize: '7px', color: 'white', fontWeight: 'bold', marginTop: '-2px' }}></span>
                                    </div>
                                </a>
                                <div className="divider"></div>
                                <button className="btm-icon-wrapper" onClick={handleEditClick} style={{ background: 'none', border: 'none', padding: 0 }}>
                                    <div className="icon-transparent" style={{ color: '#f59e0b' }}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                                    </div>
                                </button>
                                <div className="divider"></div>
                                <button className="btm-icon-wrapper" onClick={() => handleDeleteUser(selectedUser.user_id || selectedUser.id)} style={{ background: 'none', border: 'none', padding: 0 }}>
                                    <div className="icon-transparent" style={{ color: '#ef4444' }}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs and Search Bar */}
            <div className={`controls-row d-flex justify-content-between align-items-center mb-4 gap-3 flex-wrap mt-3 ${isCreateModalOpen || isEditModalOpen || selectedUser ? 'blur-background' : ''}`}>
                <div className="toggle-container m-0">
                    <button className={`toggle-btn ${activeTab === 'pramukh' ? 'active' : ''}`} onClick={() => handleTabChange('pramukh')}>
                        {txt.tabPramukh} ({boothPramukhs.length})
                    </button>
                    <button className={`toggle-btn ${activeTab === 'voters' ? 'active' : ''}`} onClick={() => handleTabChange('voters')}>
                        {txt.tabVoters} ({voters.length})
                    </button>
                </div>

                {activeTab === 'voters' ? (
                    <div className="search-filter-wrapper">
                        <div className="search-wrapper m-0">
                            <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                className="search-input"
                                type="text"
                                placeholder={txt.searchVoter}
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            />
                            {searchTerm && (
                                <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Survey Status Filter Dropdown */}
                        <div className="filter-dropdown-container">
                            <button
                                className="filter-btn"
                                onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                            >
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
                                </svg>
                                {getFilterButtonText()}
                                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </button>

                            {isFilterDropdownOpen && (
                                <div className="filter-dropdown-menu">
                                    <button
                                        className={`filter-option ${filterType === 'all' ? 'active' : ''}`}
                                        onClick={() => handleFilterClick('all')}
                                    >
                                        {txt.all}
                                    </button>
                                    <button
                                        className={`filter-option ${filterType === 'survey_available' ? 'active' : ''}`}
                                        onClick={() => handleFilterClick('survey_available')}
                                    >
                                        <span className="filter-dot available-dot"></span>
                                        {txt.surveyAvailable}
                                    </button>
                                    <button
                                        className={`filter-option ${filterType === 'survey_not_available' ? 'active' : ''}`}
                                        onClick={() => handleFilterClick('survey_not_available')}
                                    >
                                        <span className="filter-dot not-available-dot"></span>
                                        {txt.surveyNotAvailable}
                                    </button>
                                    <button
                                        className={`filter-option ${filterType === 'survey_pending' ? 'active' : ''}`}
                                        onClick={() => handleFilterClick('survey_pending')}
                                    >
                                        <span className="filter-dot pending-dot"></span>
                                        {txt.surveyPending}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="search-wrapper m-0">
                        <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            className="search-input"
                            type="text"
                            placeholder={txt.searchPramukh}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                )}
            </div>

            <div className={isCreateModalOpen || isEditModalOpen || selectedUser ? 'blur-background' : ''}>
                {activeTab === 'pramukh' && (
                    <>
                        {renderOrganizationSection(bpItems, txt.boothPramukhTitle, txt.addPramukhBtn, 'BP')}
                        {renderOrganizationSection(bsItems, txt.sahBoothPramukhTitle, txt.addSahPramukhBtn, 'BS')}
                    </>
                )}

                {activeTab === 'voters' && (
                    <>
                        <div className="row g-3">
                            {currentItems.length > 0 ? currentItems.map((voter, index) => (
                                <div className="col-12 col-md-6 col-lg-4" key={voter.id || index}>
                                    <div className="voter-card-clean h-100">
                                        <div className="voter-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <span className="serial-number">{index + 1 + indexOfFirstItem}.</span>
                                                <span className="voter-name-bold ms-2">{voter.eng_f_name} {voter.eng_surname}</span>
                                            </div>
                                            <div>
                                                {voter.voter_available === null && (
                                                    <button
                                                        className="btn-add-survey"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate('/voter_survey_form', { state: { voter: voter } });
                                                        }}
                                                    >
                                                        + {txt.addSurvey}
                                                    </button>
                                                )}
                                                {voter.voter_available === true && (
                                                    <span className="badge-status status-positive">
                                                        {getStatusFullForm(voter.voter_status)}
                                                    </span>
                                                )}
                                                {voter.voter_available === false && (
                                                    <span className="badge-status status-negative">
                                                        {voter.not_available_reason || 'Not Available'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="voter-body position-relative">
                                            <div
                                                className="map-icon-wrapper"
                                                style={{ cursor: 'pointer', zIndex: 10 }}
                                                title="View on Google Maps"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const address = voter.eng_localityid;
                                                    if (address && address !== "N/A") {
                                                        // ગુગલ મેપ્સમાં એડ્રેસ સર્ચ કરવા માટેની લિંક
                                                        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
                                                    } else {
                                                        alert("Address not available!");
                                                    }
                                                }}
                                            >
                                                <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                            </div>
                                            <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.eng_m_name || "N/A"}</span></div>
                                            <div className="info-row"><span className="label">{txt.address}</span><span className="value">{voter.eng_localityid || "N/A"}</span></div>
                                            <div className="info-row"><span className="label">{txt.serialNo}</span><span className="value">{voter.slnoinpart}</span></div>
                                            <div className="info-row" style={{ display: 'flex', alignItems: 'center' }}>
                                                <span className="label">{txt.mobile}</span>
                                                <span className="value mobile-value" style={{ flexGrow: 1 }}>{voter.contact_no || "N/A"}</span>
                                                <svg
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingVoter(voter);
                                                        setNewMobile(voter.contact_no || '');
                                                        setIsEditMobileOpen(true);
                                                    }}
                                                    style={{ cursor: 'pointer', color: '#f59e0b', width: '18px', height: '18px', flexShrink: 0 }}
                                                    fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                </svg>
                                            </div>
                                            <div className="info-row"><span className="label">{txt.idCardNo}</span><span className="value">{voter.idcard_no || "N/A"}</span></div>
                                            <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || voter.booth_no || "N/A"}</span></div>
                                            <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || "N/A"}</span></div>
                                            <div className="info-row"><span className="label">{txt.pollingLoc}</span><span className="value">{voter.eng_polling_location || "-"}</span></div>
                                        </div>
                                        <div className="voter-actions">
                                            <ActionButton icon="📞" color="#0ea5e9" label={txt.call}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!voter.contact_no || voter.contact_no === "N/A" || voter.contact_no === "-" || voter.contact_no === "") {
                                                        alert("Mobile number is not available for this voter.");
                                                    }
                                                    else {
                                                        window.open(`tel:${voter.contact_no}`, '_blank');
                                                    }
                                                }}

                                            />
                                            <ActionButton icon="✉️" color="#f59e0b" label={txt.text} onClick={() => handleSmsShare(voter)} />
                                            {/* નવો કોડ */}
                                            <WhatsappSlipButton voter={voter} />
                                            {/* નોંધ: જો map માં વેરીએબલ નું નામ member હોય, તો voter={member} પાસ કરજો */}
                                            <PracharButton voter={voter} />
                                            <ActionButton
                                                icon="🖨"
                                                color="#f97316"
                                                label={txt.print}
                                                onClick={() => navigate('/printer', { state: { voter_id: voter.id } })}
                                            />
                                            <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} onClick={() => navigate('/dis_family_member', { state: { voter_id: voter.id } })} />
                                        </div>
                                    </div>
                                </div>
                            )) : <div className="col-12 text-center p-5 text-muted">{txt.noVoters}</div>}
                        </div>
                        {filteredList.length > itemsPerPage && (
                            <div className="pagination-wrapper">
                                <div className="pagination-content">
                                    <button className="pagination-nav-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg>
                                    </button>
                                    <div className="page-numbers">
                                        {getPageNumbers().map((number, index) => (
                                            <React.Fragment key={index}>
                                                {number === '...' ? <span className="page-dots">...</span> : <button className={`page-num-btn ${currentPage === number ? 'active' : ''}`} onClick={() => paginate(number)}>{number}</button>}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    <button className="pagination-nav-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <style jsx>{`
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.6); display: flex; align-items: center; justify-content: center; z-index: 1000;}
                .modal-content-box { background: white; border-radius: 12px; width: 100%; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1); }
                .modal-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 15px; margin-bottom: 10px; }
                .close-btn-icon { background: none; border: none; cursor: pointer; color: #6b7280; }
                .close-btn-icon:hover { color: #111827; }

                .profile-detail-container { position: relative; width: 100%; max-width: 380px; margin-top: 50px; }
                .profile-close-btn { position: absolute; top: -40px; right: -10px; background: none; border: none; cursor: pointer; padding: 5px; z-index: 10; }
                .profile-detail-card { background: white; border-radius: 20px; display: flex; flex-direction: column; overflow: visible; box-shadow: 0 15px 35px rgba(0,0,0,0.2); }
                .profile-avatar-wrapper { display: flex; justify-content: center; margin-top: -55px; }
                .profile-avatar-bg { width: 110px; height: 110px; background: white; border-radius: 50%; padding: 6px; }
                .profile-avatar-bg img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
                .profile-content { padding: 15px 24px 25px 24px; text-align: center; }
                .profile-name { font-size: 22px; font-weight: 800; color: #111827; text-transform: uppercase; margin: 0; }
                .profile-mobile { font-size: 16px; color: #374151; margin: 8px 0 24px 0; letter-spacing: 0.5px; font-weight: 500; }
                .profile-big-btns { display: flex; flex-direction: column; gap: 12px; }
                .btn-resp { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; padding: 14px; border-radius: 50px; color: white; font-weight: 600; text-decoration: none; font-size: 15px; transition: transform 0.2s, box-shadow 0.2s; border: none; }
                .btn-resp:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(0,0,0,0.15); color: white; }
                .btn-wa { background-color: #4ade80; } .btn-sms-big { background-color: #f59e0b; }
                
                .profile-bottom-bar { background-color: #213555; border-radius: 0 0 20px 20px; display: flex; justify-content: space-evenly; align-items: center;}
                .divider { width: 1px; height: 24px; background-color: rgba(255, 255, 255, 0.3); }
                .btm-icon-wrapper { background: none; border: none; padding: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: transform 0.2s; }
                .btm-icon-wrapper:hover { transform: scale(1.1); }
                .icon-circle { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; }
                .bg-call { background-color: #0ea5e9; } .bg-wa-small { background-color: #22c55e; }
                .icon-transparent { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; position: relative; }
                .color-sms { color: #3b82f6; } .color-edit { color: #f59e0b; } .color-delete { color: #ef4444; }

                .desktop-view { display: block; } .mobile-view { display: none; }
                @media (max-width: 768px) { .desktop-view { display: none; } .mobile-view { display: block; } }
                
                .controls-row { background: #fff; padding: 12px 16px; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); border: 1px solid #f1f5f9; }
                .search-wrapper { max-width: 400px; flex: 1; min-width: 250px; position: relative; }
                .search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 18px; height: 18px; pointer-events: none; }
                .search-input { width: 100%; padding: 12px 16px 12px 46px; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 14px; color: #1e293b; background-color: #ffffff; outline: none; transition: all 0.2s ease; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .search-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }
                .clear-search-btn {
                    position: absolute;
                    right: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: #f1f5f9;
                    border: none;
                    border-radius: 50%;
                    padding: 4px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #64748b;
                }
                .clear-search-btn:hover { background: #e2e8f0; }
                
                .search-filter-wrapper {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    flex: 1;
                }
                .search-filter-wrapper .search-wrapper {
                    flex: 1;
                    max-width: none;
                }
                
                /* Filter Dropdown Styles */
                .filter-dropdown-container {
                    position: relative;
                }
                .filter-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 16px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #1e293b;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                }
                .filter-btn:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                }
                .filter-dropdown-menu {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    margin-top: 8px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
                    min-width: 180px;
                    z-index: 100;
                    overflow: hidden;
                }
                .filter-option {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    width: 100%;
                    padding: 10px 16px;
                    border: none;
                    background: white;
                    font-size: 13px;
                    font-weight: 500;
                    color: #334155;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-align: left;
                }
                .filter-option:hover {
                    background: #f1f5f9;
                }
                .filter-option.active {
                    background: #eff6ff;
                    color: #2563eb;
                }
                .filter-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    display: inline-block;
                }
                .available-dot { background-color: #22c55e; }
                .not-available-dot { background-color: #ef4444; }
                .pending-dot { background-color: #f59e0b; }

                /* Survey Status Styles */
                .btn-add-survey {
                    background-color: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 16px;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 4px 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                }
                .btn-add-survey:hover {
                    background-color: #2563eb;
                    transform: translateY(-1px);
                }
                .badge-status {
                    display: inline-block;
                    border-radius: 16px;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 4px 12px;
                    white-space: nowrap;
                    display: inline-flex;
                    align-items: center;
                }
                .status-positive {
                    background-color: #dcfce7;
                    color: #166534;
                    border: 1px solid #bbf7d0;
                }
                .status-negative {
                    background-color: #fee2e2;
                    color: #991b1b;
                    border: 1px solid #fecaca;
                }

                .toggle-container { display: flex; background-color: #e2e8f0; border-radius: 12px; padding: 6px; border: none; box-shadow: inset 0 2px 4px rgba(0,0,0,0.05); }
                .toggle-btn { padding: 10px 24px; border: none; border-radius: 8px; background: transparent; color: #475569; font-weight: 700; font-size: 14px; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); white-space: nowrap; }
                .toggle-btn:hover:not(.active) { color: #0f172a; }
                .toggle-btn.active { background-color: #2563EB; color: #ffffff; box-shadow: 0 4px 10px rgba(37, 99, 235, 0.3); }

                .avatar-circle { 
                    width: 40px; height: 40px; border-radius: 50%; overflow: hidden; 
                    background: #f3f4f6; display: flex; align-items: center; justify-content: center; border: 1px solid #e5e7eb;
                }
                .avatar-img { width: 100%; height: 100%; object-fit: cover; }
                .avatar-placeholder { 
                    font-weight: 700; color: #64748b; font-size: 16px; 
                    display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;
                }

                .badge-designation { background: #eff6ff; color: #1e40af; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 600; display: inline-block; }
                .action-circle-btn { border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: 0.2s; text-decoration: none; width: 32px; height: 32px; }
                .btn-blue { background: #e0f2fe; color: #0284c7; } .btn-green { background: #dcfce7; color: #166534; }
                
                .pagination-wrapper { display: flex; justify-content: center; padding: 20px 0; position: sticky; bottom: 20px; z-index: 50; pointer-events: none; }
                .pagination-content { background: white; border: 1px solid #E2E8F0; border-radius: 50px; padding: 8px 16px; display: flex; align-items: center; gap: 8px; box-shadow: 0 10px 25px rgba(0,0,0,0.1); pointer-events: auto; }
                .pagination-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: none; background: transparent; color: #2563EB; cursor: pointer; display: flex; align-items: center; justify-content: center; }
                .page-num-btn { width: 32px; height: 32px; border-radius: 50%; border: 1px solid transparent; background: transparent; color: #475569; font-weight: 600; font-size: 14px; cursor: pointer; }
                .page-num-btn.active { background: #2563EB; color: white; box-shadow: 0 2px 4px rgba(37, 99, 235, 0.3); }
                .page-dots { color: #94A3B8; font-weight: bold; padding: 0 4px; }

                .section-title-fixed { font-size: 1.2rem; font-weight: 700; color: #1e293b; border-left: 4px solid #2563eb; padding-left: 12px; }
                .btn-add-user-small { 
                    display: flex; align-items: center; gap: 6px; padding: 8px 14px; 
                    background: #2563eb; color: white; border: none; border-radius: 8px; 
                    font-size: 13px; font-weight: 600; cursor: pointer; transition: 0.2s;
                }
                .btn-add-user-small:hover { background: #1d4ed8; transform: translateY(-1px); }
                .blur-background { filter: blur(4px); pointer-events: none; }

                @media (max-width: 768px) {
                    .controls-row { flex-direction: column; align-items: stretch; gap: 12px; }
                    .toggle-container { width: 100%; justify-content: center; }
                    .toggle-btn { flex: 1; text-align: center; }
                    .search-filter-wrapper { flex-direction: column; width: 100%; }
                    .search-wrapper { width: 100%; max-width: none; }
                    .filter-dropdown-container { width: 100%; }
                    .filter-btn { justify-content: center; width: 100%; }
                    .filter-dropdown-menu { right: auto; left: 0; width: 100%; }
                    .pagination-content { padding: 6px 12px; gap: 4px; transform: scale(0.9); }
                    .page-num-btn { width: 28px; height: 28px; font-size: 12px; }
                    .btn-add-survey { font-size: 10px; padding: 3px 10px; }
                    .badge-status { font-size: 10px; padding: 3px 10px; }
                }
                .subtitle-container {
                    margin-top: 8px;
                    margin-bottom: 20px;
                    position: relative;
                }
                .subtitle-text {
                    font-size: 15px;
                    color: #334155;
                    margin: 0 0 10px 0;
                    font-weight: 600;
                }
                .subtitle-container::after {
                    content: "";
                    display: block;
                    width: 60px;
                    height: 3px;
                    background: linear-gradient(to right, #2563eb, #60a5fa);
                    border-radius: 3px;
                    margin-top: 4px;
                }
            `}</style>
        </div>
    );
};

const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }

    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default OrganizationDetail;